import React, { useState, useEffect } from 'react'
import { Grid, Tab } from '@material-ui/core'
import { TabContext, TabList, TabPanel, Pagination } from '@material-ui/lab'
import { useDispatch, useSelector } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import { CoreNavigation } from 'src/components/core'
import { useMediaItems } from './hooks/useMediaItems'
import { useAiState } from './hooks/useAiState'
import { useFileHandling } from './hooks/useFileHandling'
import { MediaUploader } from './media-uploader'
import { SearchBar } from './search-bar'
import MediaGrid from './media-grid'
import Giphy from './giphy'
import Unsplash from './unsplash'
import EmbedImage from './embed-image'
import AiImage from './ai-image-generation'
import { mediaDirectoryGetRoot } from 'src/reducers/media-directory/action'
import { mediaImageUploadAction } from 'src/reducers/media-image/action'
import style from './style'
import { tabList } from './data'

const ImageGallery = ({
  classes,
  cardEntity,
  popupType,
  onOk,
  setImageURL,
  assignment,
  type,
}) => {
  const dispatch = useDispatch()
  const entity = useSelector(state => state.mediaDirectory.entity)

  const [page, setPage] = useState(1)
  const [filterText, setFilterText] = useState(null)
  const [tabValue, setTabValue] = useState(type ? 6 : 1)
  const [folderId, setFolderId] = useState(null)

  const { items, handleSelect, addItem } = useMediaItems(setImageURL)
  const { aiState, setAiCount, handleLoading, fetchLimits } = useAiState()
  const { getMediaItemType } = useFileHandling()

  useEffect(() => {
    if (items.length > 0) {
      onOk(items)
    }
  }, [items, onOk])

  useEffect(() => {
    fetchLimits()
  }, [fetchLimits])

  useEffect(() => {
    dispatch(mediaDirectoryGetRoot(page, filterText))
  }, [dispatch, page, filterText])

  const handleTabChange = newValue => {
    setTabValue(newValue)
  }

  const handleFileUpload = file => {
    const mediaType = getMediaItemType(file.type)
    if (!mediaType) return

    dispatch(mediaImageUploadAction(file, folderId, data => addItem(data.url)))
  }

  const refreshList = () => {
    dispatch(mediaDirectoryGetRoot(page, filterText))
  }

  // Get the tabList array by calling the function
  const navigationTabs = tabList(handleTabChange, tabValue)

  return (
    <div className={classes.root}>
      <TabContext value={tabValue}>
        <CoreNavigation
          tabList={navigationTabs}
          color="secondary"
          backgroundColor="primary"
          size="small"
          loading={false}
        />

        <TabPanel
          value={tabValue}
          index={1}
          hidden={tabValue !== 1}
          className={classes.tabPanel}
        >
          <div className={classes.content}>
            <div className={classes.headerRow}>
              <SearchBar value={filterText} onChange={setFilterText} />
              <MediaUploader onUpload={handleFileUpload} classes={classes} />
            </div>

            <div className={classes.mediaContainer}>
              <MediaGrid
                items={entity?.results}
                onSelect={handleSelect}
                dispatch={dispatch}
                classes={classes}
                refreshList={refreshList}
              />
            </div>

            {entity?.totalPages > 0 && (
              <div className={classes.paginationContainer}>
                <Pagination
                  count={entity.totalPages}
                  page={page}
                  onChange={(e, value) => {
                    // Prevent setting invalid page numbers (like -1 from ellipsis clicks)
                    if (value > 0 && value <= entity.totalPages) {
                      setPage(value)
                    }
                  }}
                />
              </div>
            )}
          </div>
        </TabPanel>

        <TabPanel
          value={tabValue}
          index={6}
          hidden={tabValue !== 6}
          className={classes.tabPanel}
        >
          <AiImage
            cardEntity={cardEntity}
            assignment={assignment}
            onPhotoClick={addItem}
            setAiCount={setAiCount}
            dispatch={dispatch}
            count={aiState.count}
            limit={aiState.limit}
            loading={aiState.loading}
            handleLoading={handleLoading}
            refreshedAt={aiState.refreshedAt}
            popupType={popupType}
          />
        </TabPanel>

        <TabPanel
          value={tabValue}
          index={3}
          hidden={tabValue !== 3}
          className={classes.tabPanel}
        >
          <Giphy
            onGifClick={gif => addItem(gif.images.original.url)}
            classes={classes}
          />
        </TabPanel>

        <TabPanel
          value={tabValue}
          index={4}
          hidden={tabValue !== 4}
          className={classes.tabPanel}
        >
          <Unsplash onPhotoClick={addItem} />
        </TabPanel>

        <TabPanel
          value={tabValue}
          index={5}
          hidden={tabValue !== 5}
          className={classes.tabPanel}
        >
          <EmbedImage onPhotoClick={addItem} />
        </TabPanel>
      </TabContext>
    </div>
  )
}

export default withStyles(style, { withTheme: true })(ImageGallery)
